import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Search,
  ChevronDown,
  Link as LinkIcon,
} from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import { getDocSections, getPageStrings } from './docs-content';
import type { DocSection } from './docs-content';

// ---------------------------------------------------------------------------
// DocsPage component
// ---------------------------------------------------------------------------

export function DocsPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'ro') as 'ro' | 'ru' | 'en';
  // Normalize lang to one of the supported values
  const normalizedLang = (['ro', 'ru', 'en'].includes(lang) ? lang : 'ro') as 'ro' | 'ru' | 'en';

  const sections: DocSection[] = useMemo(() => getDocSections(normalizedLang), [normalizedLang]);
  const strings = useMemo(() => getPageStrings(normalizedLang), [normalizedLang]);

  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? 'overview');
  const [tocOpen, setTocOpen] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Filter sections by search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const q = searchQuery.toLowerCase();
    return sections.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.id.toLowerCase().includes(q),
    );
  }, [searchQuery, sections]);

  // Scroll spy: observe which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    const refs = sectionRefs.current;
    for (const section of sections) {
      const el = refs[section.id];
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sections]);

  const scrollToSection = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
      setTocOpen(false);
    }
  };

  const copyAnchorLink = (id: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    void navigator.clipboard.writeText(url);
  };

  // On mount, scroll to hash if present
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => scrollToSection(hash), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-full min-h-0">
      {/* Desktop TOC sidebar */}
      <aside className="hidden w-64 flex-shrink-0 overflow-y-auto border-r border-slate-200 bg-white p-4 lg:block dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-4">
          <div className="flex items-center gap-2 text-forest-700 dark:text-forest-300">
            <BookOpen className="h-5 w-5" />
            <h2 className="text-sm font-bold uppercase tracking-wider">{strings.sidebarTitle}</h2>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={strings.searchPlaceholder}
            className="w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-xs text-slate-700 placeholder:text-slate-400 focus:border-forest-400 focus:outline-none focus:ring-1 focus:ring-forest-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-forest-500 dark:focus:ring-forest-500"
          />
        </div>

        {/* TOC links */}
        <nav className="space-y-0.5">
          {filteredSections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => scrollToSection(section.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition-colors',
                  isActive
                    ? 'bg-forest-50 text-forest-700 dark:bg-forest-900/30 dark:text-forest-200'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200',
                )}
              >
                <Icon
                  className={cn(
                    'h-3.5 w-3.5 flex-shrink-0',
                    isActive
                      ? 'text-forest-600 dark:text-forest-300'
                      : 'text-slate-400 dark:text-slate-500',
                  )}
                />
                <span className="truncate">{section.title}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main content area */}
      <main className="flex-1 overflow-y-auto">
        {/* Mobile TOC dropdown */}
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-2 lg:hidden dark:border-slate-700 dark:bg-slate-900">
          <button
            type="button"
            onClick={() => setTocOpen(!tocOpen)}
            className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-forest-600 dark:text-forest-300" />
              {sections.find((s) => s.id === activeSection)?.title ?? strings.sidebarTitle}
            </span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-slate-400 transition-transform dark:text-slate-500',
                tocOpen && 'rotate-180',
              )}
            />
          </button>

          {tocOpen && (
            <div className="absolute left-4 right-4 z-20 mt-1 rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
              {/* Mobile search */}
              <div className="border-b border-slate-100 p-2 dark:border-slate-800">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={strings.searchPlaceholderShort}
                    className="w-full rounded-md border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-xs text-slate-700 placeholder:text-slate-400 focus:border-forest-400 focus:outline-none focus:ring-1 focus:ring-forest-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
                  />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto p-1">
                {filteredSections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                        activeSection === section.id
                          ? 'bg-forest-50 text-forest-700 dark:bg-forest-900/30 dark:text-forest-200'
                          : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800',
                      )}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0 text-slate-400 dark:text-slate-500" />
                      {section.title}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Doc content */}
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Page header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              {strings.pageTitle}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {strings.pageSubtitle}
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {filteredSections.map((section) => {
              const Icon = section.icon;
              return (
                <div
                  key={section.id}
                  id={section.id}
                  ref={(el) => {
                    sectionRefs.current[section.id] = el;
                  }}
                  className="scroll-mt-20"
                >
                <Card>
                  <CardBody className="p-6 sm:p-8">
                    {/* Section header with anchor */}
                    <div className="group mb-4 flex items-center gap-3">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-forest-100 text-forest-700 dark:bg-forest-900/40 dark:text-forest-300">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        {section.title}
                      </h2>
                      <button
                        type="button"
                        onClick={() => copyAnchorLink(section.id)}
                        className="ml-1 rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-500 group-hover:opacity-100 dark:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                        title={strings.copyLinkTitle}
                        aria-label={strings.copyLinkTitle}
                      >
                        <LinkIcon className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Section content */}
                    {section.content}
                  </CardBody>
                </Card>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-12 border-t border-slate-200 pt-6 text-center dark:border-slate-700">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {strings.footerLine1}
            </p>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              {strings.footerLine2}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
